import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { useMyContext } from "../../context/MyContext";
import { useNavigate, useLocation } from 'react-router-dom';
import TourSidebar from './TourSidebar';
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../config/firebase";

const WelcomeTour = ({ onComplete }) => {
  const {
    user,
    completeWelcomeTour,
    welcomeTourActive,
    setWelcomeTourActive,
    setWelcomeTourChecked,
  } = useMyContext();

  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);
  const requestAnimationRef = useRef(null);
  const previousElementRef = useRef(null);
  const tooltipsRef = useRef(new Map());
  const initAttempts = useRef(0);
  const maxInitAttempts = 5;

  const tourSteps = [
    {
      title: "Welcome to Your Dashboard",
      description: "Your central hub for managing global shipments. Track totals, monitor ongoing and delivered shipments and view real-time status updates directly on the interactive world globe.",
      elementId: "dashboard",
      path: "/dashboard"
    },
    {
      title: "New Requests",
      description: "Quickly create and manage quote requests, review TradeOS™ calculations and review proposals.",
      elementId: "bookshipment",
      path: "/bookshipment"
    },
    {
      title: "Quote Request",
      description: "View and manage your manual quote requests. Track request status, review submission details, and create a new request when needed.",
      elementId: "quote-request-tab",
      path: "/bookshipment",
      subTab: "request"
    },
    {
      title: "New Request",
      description: "Start a new manual quote request. Enter your shipment details and submit the information for your quote inquiry.",
      elementId: "new-request-button",
      path: "/bookshipment",
      subTab: "request"
    },
    {
      title: "TradeOS™ Quotations",
      description: "Access and manage your TradeOS quotations for your shipments. Review detailed cost breakdowns and take action — including viewing, downloading, accepting, or canceling your quotation.",
      elementId: "proposal-tab",
      path: "/bookshipment",
      subTab: "proposal"
    },
    {
      title: "Proposals Tab",
      description: "View your accepted proposals for IOR estimates. These are the estimates you have approved and can proceed with.",
      elementId: "ior-estimate-tab",
      path: "/bookshipment",
      subTab: "estimate"
    },
    {
      title: "New Quotes",
      description: "Access your finalized quotations approved by our team. Review quote numbers, routes, and documentation, and manage your quotation — including viewing, downloading, accepting, canceling or direct communication with our team.",
      elementId: "newquotes",
      path: "/newquotes"
    },
    {
      title: "View Your Quotes",
      description: "Access your quotation history, including accepted and canceled quotations. Review status details, download documentation, and maintain a complete record for reference.",
      elementId: "myqoutes",
      path: "/myqoutes"
    },
    {
      title: "My Shipments",
      description: "Get a complete overview of all ongoing shipments — with full shipment details in one place. View order and tracking numbers, monitor shipment status and ETAs in real time and download your shipment labels.",
      elementId: "myShipment",
      path: "/myShipment",
    },
    {
      title: "Shipment Tracking",
      description: "Access detailed shipment information using your tracking number. View real-time status updates, estimated arrival dates, and download shipment, customs, or export documentation uploaded by our team.",
      elementId: "trackshipment",
      path: "/trackshipment",
    },
    {
      title: "TradeOS",
      description: "Generate complete quotations and compliance breakdowns for your IOR and EOR shipments. Use TradeOS to analyze duties, taxes, service costs and compliance overview before proceeding with your shipment.",
      elementId: "TradeOS",
      path: "/oneunion-trade-os",
    },
    {
      title: "OneScan™",
      description: "Built in-house by One Union Solutions to eliminate manual data entry from lengthy commercial invoices. OneScan™ extracts key shipment details automatically and pre-fills TradeOS — saving time and reducing effort.",
      elementId: "onescan-button",
      path: "/oneunion-trade-os",
    },
    {
      title: "HS Compass™",
      description: "Built in-house by One Union Solutions, HS Compass™ identifies highly accurate the local HS Code based on your product details and target country. Powered by real customs data, it provides full classification reasoning and allows you to download a structured HS Code report.",
      elementId: "HS Compass",
      path: "/hs-compass",
    },
    {
      title: "Invoices",
      description: "Access a comprehensive overview of all issued invoices associated with your account. Review total invoice amounts, outstanding balances, and paid amounts at a glance, and monitor detailed payment status for each invoice. Use search and export functions to manage, download, and analyze your billing records efficiently.",
      elementId: "invoices",
      path: "/invoices",
    },
    {
      title: "Shipment History",
      description: "Access the record of your past shipments. Review associated quote numbers, order references, invoice dates, payment status, and download related documentation from a centralized archive.",
      elementId: "shipmenthistory",
      path: "/shipmenthistory",
    },
    {
      title: "Support & Help",
      description: "Submit support tickets for operational or account-related inquiries. Track existing requests, review responses, and communicate with our team live and directly within the ticketing system.",
      elementId: "support&help",
      path: "/support&help",
    }
    ,
    {
      title: "Tutorials",
      description: "Watch short videos to quickly learn TradeOS™, HS Compass™, tracking, invoices, and more.",
      elementId: "tutorials",
      path: "/tutorials",
    }
  ];

  const handleComplete = async () => {
    cleanupTooltips();
    if (user?.uid) {
      await completeWelcomeTour(user.uid);
    }
    if (onComplete) onComplete();
  };

  // Sync step from URL so /bookshipment?tab=X matches the correct tour step
  useEffect(() => {
    if (welcomeTourActive) {
      const currentPath = location.pathname;
      const tabParam = new URLSearchParams(location.search).get('tab');
      let stepIndex;
      if (currentPath === '/bookshipment') {
        stepIndex = tabParam === 'request' ? 2 : tabParam === 'proposal' ? 4 : tabParam === 'estimate' ? 5 : 1;
        if (tabParam === 'request' && currentStep === 3) return; // keep New Request button step, don't sync back to tab step
      } else {
        stepIndex = tourSteps.findIndex(step => step.path === currentPath);
      }
      if (stepIndex !== -1 && stepIndex !== currentStep) {
        setCurrentStep(stepIndex);
      }
    }
  }, [location.pathname, location.search, welcomeTourActive]);

  useEffect(() => {
    const tourCompleted = localStorage.getItem('welcomeTourCompleted');
    const isEligible = user?.role === 'user';
    setShowTour(isEligible && !tourCompleted);
  }, [user]);

  // Actually switch the bookshipment tab via URL so NewRequest's activeTab updates (no fake highlight)
  useEffect(() => {
    const currentItem = tourSteps[currentStep];
    if (currentItem?.path === '/bookshipment' && currentItem.subTab) {
      const tab = currentItem.subTab;
      const desired = `/bookshipment?tab=${tab}`;
      const current = location.pathname + (location.search || '');
      if (current !== desired) {
        navigate(desired, { replace: true });
      }
    }
  }, [currentStep]);

  const cleanupTooltips = () => {
    if (tooltipRef.current) {
      tooltipRef.current.style.opacity = '0';
      tooltipRef.current.style.visibility = 'hidden';
    }

    tooltipsRef.current.forEach((tooltip) => {
      if (tooltip) {
        tooltip.style.opacity = '0';
        tooltip.style.visibility = 'hidden';
      }
    });

    tooltipsRef.current.clear();

    if (previousElementRef.current) {
      previousElementRef.current.classList.remove('relative', 'z-50');
      previousElementRef.current = null;
    }
  };

  const calculatePosition = () => {
    if (requestAnimationRef.current) {
      cancelAnimationFrame(requestAnimationRef.current);
    }

    requestAnimationRef.current = requestAnimationFrame(() => {
      const currentItem = tourSteps[currentStep];
      let element;

      if (['quote-request-tab', 'proposal-tab', 'ior-estimate-tab', 'new-request-button', 'onescan-button'].includes(currentItem.elementId)) {
        element = document.querySelector(`[data-tour-id="${currentItem.elementId}"]`);
      } else {
        element = document.querySelector(`[data-tour-id="${currentItem.path.slice(1)}"]`);
      }

      if (!element || !tooltipRef.current) {
        if (initAttempts.current < maxInitAttempts) {
          initAttempts.current++;
          setTimeout(calculatePosition, 100);
        }
        return;
      }

      cleanupTooltips();

      element.classList.add('relative', 'z-50');
      previousElementRef.current = element;

      const elementRect = element.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Simple clean positioning — always to the right
      let left = elementRect.right + 30;
      let top = elementRect.top + (elementRect.height / 2) - (tooltipRect.height / 2);

      // Keep inside viewport
      if (top < 30) top = 30;
      if (top + tooltipRect.height > viewportHeight - 30) top = viewportHeight - tooltipRect.height - 30;
      if (left + tooltipRect.width > viewportWidth - 30) left = elementRect.left - tooltipRect.width - 30;

      setTooltipPosition({ top, left });
      setIsTooltipVisible(true);
      tooltipRef.current.style.opacity = '1';
      tooltipRef.current.style.visibility = 'visible';
    });
  };

  useEffect(() => {
    const currentItem = tourSteps[currentStep];
    const isBookShipmentTab = currentItem?.path === '/bookshipment' && currentItem?.subTab;
    const delay = isBookShipmentTab ? 450 : 300; // allow tab content to render after URL change
    const calculatePositionWithDelay = () => {
      setTimeout(calculatePosition, delay);
    };
    calculatePositionWithDelay();
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('resize', calculatePosition);
      if (requestAnimationRef.current) cancelAnimationFrame(requestAnimationRef.current);
      cleanupTooltips();
    };
  }, [currentStep]);

  useEffect(() => {
    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    return () => {
      window.removeEventListener('resize', calculatePosition);
      if (requestAnimationRef.current) cancelAnimationFrame(requestAnimationRef.current);
      cleanupTooltips();
    };
  }, [currentStep]);

  const handleNext = async () => {
    cleanupTooltips();

    const currentItem = tourSteps[currentStep];
    const nextItem = tourSteps[currentStep + 1];

    if (currentStep === tourSteps.length - 1) {
      try {
        if (user?.uid) await completeWelcomeTour(user.uid);

        cleanupTooltips();
        setShowTour(false);
        setWelcomeTourActive(false);
        setWelcomeTourChecked(true);

        localStorage.setItem('welcomeTourCompleted', 'true');

        if (onComplete) onComplete();

        navigate(currentItem.path || '/dashboard');
      } catch (error) {
        console.error("Error completing welcome tour:", error);
      }
      return;
    }

    if (currentItem.path !== nextItem.path) {
      navigate(nextItem.path);
    } else if (nextItem.path === '/bookshipment' && nextItem.subTab) {
      navigate(`/bookshipment?tab=${nextItem.subTab}`, { replace: true });
    }

    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    cleanupTooltips();

    const currentItem = tourSteps[currentStep];
    const prevItem = tourSteps[currentStep - 1];

    if (currentStep > 0) {
      if (currentItem.path !== prevItem.path) {
        navigate(prevItem.path);
      } else if (prevItem.path === '/bookshipment') {
        navigate(prevItem.subTab ? `/bookshipment?tab=${prevItem.subTab}` : '/bookshipment', { replace: true });
      }

      setCurrentStep(prev => prev - 1);
    }
  };

  // Ensure tour persists across page reloads and matches URL (bookshipment tab)
  useEffect(() => {
    const tourCompleted = localStorage.getItem('welcomeTourCompleted');
    const shouldShowTour = user?.role === 'user' && !tourCompleted;

    if (shouldShowTour) {
      const currentPath = location.pathname;
      const tabParam = new URLSearchParams(location.search).get('tab');
      const stepIndex = currentPath === '/bookshipment'
        ? (tabParam === 'request' ? 2 : tabParam === 'proposal' ? 4 : tabParam === 'estimate' ? 5 : 1)
        : tourSteps.findIndex(step => step.path === currentPath);

      if (stepIndex !== -1) setCurrentStep(stepIndex);
    }
  }, [location.pathname, location.search, user]);

  if (!welcomeTourActive || user?.role !== 'user') return null;

  const currentItem = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50" >
      <div className="relative w-full h-full">
        <TourSidebar currentStep={currentStep} tourSteps={tourSteps} />

        <button
          onClick={handleComplete}
          className="absolute top-6 right-6 text-white hover:text-gray-200 flex items-center gap-2 px-6 py-3 rounded-full bg-gray-800/80 shadow-lg transition-all duration-300 hover:bg-gray-700/80 hover:scale-105"
        >
          Skip Tour <X size={20} className="ml-1" />
        </button>

        <div
          ref={tooltipRef}
          className={`absolute transition-all duration-500 ${isTooltipVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-1 bg-gradient-to-b from-violet-500 to-fuchsia-500 rounded-full" />
              <h3 className="font-bold text-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
                {currentItem.title}
              </h3>
            </div>
            <p className="text-gray-600 text-lg leading-relaxed">
              {currentItem.description}
            </p>
          </div>
        </div>

        <div className="absolute bottom-12 right-12 flex items-center gap-4">
          <button
            onClick={handlePrev}
            className={`flex items-center gap-3 px-8 py-4 rounded-full transition-all duration-300 transform ${isFirstStep
              ? 'invisible'
              : 'bg-white hover:bg-gray-50 shadow-lg hover:scale-105 active:scale-95'
              }`}
          >
            <ChevronLeft size={24} className="text-violet-600" />
            <span className="font-semibold text-gray-700">Previous</span>
          </button>

          <button
            onClick={handleNext}
            className="flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95"
          >
            <span className="font-semibold">{isLastStep ? 'Finish' : 'Next'}</span>
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 flex gap-3">
          {tourSteps.map((_, index) => (
            <div
              key={index}
              className={`transition-all duration-300 rounded-full ${index === currentStep
                ? 'w-8 h-2 bg-gradient-to-r from-violet-600 to-fuchsia-500'
                : 'w-2 h-2 bg-white/60 hover:bg-white/80'
                }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;